/**
 * API Service Layer for Founders' Award App
 * 
 * WIRING GUIDE FOR WARP.DEV:
 * ---------------------------
 * This file contains all the integration points. Replace the mock implementations
 * with real Azure calls. The Azure Functions in /api/ handle the server-side logic.
 * 
 * Required Azure Resources:
 * 1. Azure Blob Storage Account
 *    - Container: "founders-config" (stores config.json)
 *    - Container: "founders-submissions" (stores submission data + files)
 * 2. Azure Static Web App (deploy this frontend)
 * 3. Azure Functions (deploy /api/ folder - auto-deployed with Static Web App)
 * 
 * Environment Variables (set in Static Web App > Configuration):
 *    AZURE_STORAGE_CONNECTION_STRING
 *    AZURE_STORAGE_ACCOUNT_NAME  
 *    AZURE_STORAGE_ACCOUNT_KEY
 *    ADMIN_PASSWORD_HASH (bcrypt hash of the admin password)
 *    SAS_TOKEN_EXPIRY_MINUTES=60
 */

const API_BASE = '/api';

// ============================================================
// DEFAULT CONFIG - Used to seed the config blob on first run
// ============================================================

export const DEFAULT_CONFIG = {
  awardYear: "2026",
  awardTitle: "The Founders' Award",
  logoUrl: "https://saxadvisorygroup.com/wp-content/uploads/2024/08/Sax_4-hor-white-5.png",
  heroImageUrl: "",
  theme: "Perseverance",
  themeDefinition: "Doing something despite difficulty or delay in achieving success.",
  heroSubtitle: "Your nonprofit has the chance to win $20,000.",
  introHtml: `<p>SAX Founders – Sander Sax, Morris Macy, and Egon Fromm – inspired the vision of advancing clients forward by advising them in the key areas critical to their success. This is the cornerstone of how SAX does business today.</p>
<p>SAX is a champion of nonprofits and The Founders' Award is a vehicle in which we can honor the values of our Founders by empowering and supporting those organizations in ways that ensure their continued existence and positive impact on our world.</p>`,
  videoInstructions: "Non-professional video, no longer than 5 minutes in length, answering the following questions. Please begin each video stating your organization's mission.",
  submissionDeadline: "2026-06-20T23:59:00",
  submissionsOpen: true,
  eventDate: "Thursday, September 24, 2026",
  eventTime: "8:30-11:30 AM",
  eventLocation: "Northern NJ",
  contactEmail: "foundersaward@saxadvisorygroup.com",
  faqUrl: "",
  prizes: [
    { place: "1st Place", description: "$20,000 unrestricted gift" },
    { place: "2nd Place", description: "Mission Moment standalone video provided by Beard and Bowler Productions" },
    { place: "3rd Place", description: "$5,000 unrestricted gift, courtesy of Valley Bank" }
  ],
  videoQuestions: [
    "How is perseverance engrained in your organization's mission?",
    "Can you share a time when your nonprofit faced a major challenge and how you overcame it?",
    "How do you plan to sustain and grow your impact despite obstacles like funding or policy changes?"
  ],
  formFields: [
    { id: "orgName", label: "Organization Name", type: "text", required: true },
    { id: "orgWebsite", label: "Organization Website", type: "url", required: false },
    { id: "orgAddress", label: "Organization Address", type: "text", required: true },
    { id: "orgCity", label: "City", type: "text", required: true },
    { id: "orgState", label: "State", type: "text", required: true },
    { id: "orgZip", label: "Zip Code", type: "text", required: true },
    { id: "contactName", label: "Primary Contact Name", type: "text", required: true },
    { id: "contactTitle", label: "Contact Title", type: "text", required: true },
    { id: "contactEmail", label: "Contact Email", type: "email", required: true },
    { id: "contactPhone", label: "Contact Phone", type: "tel", required: true },
    { id: "execName", label: "Executive Director / C-Suite Name", type: "text", required: true },
    { id: "execTitle", label: "Executive Director / C-Suite Title", type: "text", required: true },
    { id: "execEmail", label: "Executive Director / C-Suite Email", type: "email", required: true },
    { id: "orgMission", label: "Organization Mission Statement", type: "textarea", required: true },
    { id: "orgEin", label: "EIN / Tax ID Number", type: "text", required: false },
  ],
  maxVideoSizeMB: 51200,
  maxLogoSizeMB: 25,
  acceptedVideoTypes: ".mp4,.mov,.avi,.wmv,.mkv,.webm",
  acceptedLogoTypes: ".png,.jpg,.jpeg,.svg,.gif,.webp",
  consentTop75: "In the case of us making the Top 75, please use the address and email above to send the invitation to the 2024 Founders' Award event.",
  consentVideo: "I confirm that I have received consent from the beneficiaries in the video and photos we have submitted to use and/or show at the Founders' Award event.",
  consentCsuite: "I understand one (1) C-suite executive with the title of President, CEO, COO, CFO or Executive Director, or a Board member must attend the Founders' Award event on Tuesday, September 10, 2024 to receive the award should we be selected as the winner.",
};


// ============================================================
// CONFIG API
// ============================================================

/**
 * Fetch the current site configuration.
 * WIRE: GET /api/config → reads config.json from "founders-config" blob container
 */
export async function getConfig() {
  try {
    const res = await fetch(`${API_BASE}/config`);
    if (!res.ok) throw new Error('Failed to load config');
    return await res.json();
  } catch (err) {
    console.warn('Config fetch failed, using defaults:', err.message);
    return DEFAULT_CONFIG;
  }
}

/**
 * Update the site configuration.
 * WIRE: PUT /api/config → writes config.json to "founders-config" blob container
 * Requires admin auth token in header.
 */
export async function updateConfig(config) {
  const res = await fetch(`${API_BASE}/config`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(config)
  });
  if (!res.ok) throw new Error('Failed to update config');
  return await res.json();
}


// ============================================================
// ADMIN AUTH API
// ============================================================

// adminLogin removed — authentication is now handled by Azure Entra (AAD) via
// the SWA built-in auth provider. The /.auth/login/aad flow handles sign-in.


// ============================================================
// SUBMISSION API  
// ============================================================

/**
 * Submit the application form data (text fields only).
 * Returns a submission ID and SAS URLs for file uploads.
 * 
 * WIRE: POST /api/submit → 
 *   1. Validate submission deadline hasn't passed
 *   2. Create folder in "founders-submissions" container: {orgName-timestamp}/
 *   3. Save submission.json with form data + timestamp
 *   4. Generate SAS upload URLs for logo + video blobs
 *   5. Return { submissionId, logoUploadUrl, videoUploadUrl }
 */
export async function submitApplication(formData) {
  // Add browser/client info for tracking
  const clientInfo = {
    userAgent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform,
    screenSize: `${window.screen.width}x${window.screen.height}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  };

  const res = await fetch(`${API_BASE}/submitform`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...formData, clientInfo })
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Submission failed');
  }
  return await res.json();
}

/**
 * Upload a file directly to Azure Blob Storage using a SAS URL.
 * This goes direct to blob (not through the API) for large file support.
 * Uses chunked upload for files over 100MB to handle up to 50GB videos.
 * Each block is retried up to MAX_RETRIES times with exponential backoff.
 */
const MAX_RETRIES = 3;
const RETRY_BASE_DELAY_MS = 1000;

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

async function putWithRetry(url, body, headers, label) {
  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    try {
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', url, true);
        Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else reject(new Error(`${label} failed (HTTP ${xhr.status})${xhr.responseText ? ': ' + xhr.responseText.slice(0, 200) : ''}`));
        };
        xhr.onerror = () => reject(new Error(`${label} network error (attempt ${attempt}/${MAX_RETRIES})`));
        xhr.send(body);
      });
      return; // success
    } catch (err) {
      if (attempt === MAX_RETRIES) throw err;
      await sleep(RETRY_BASE_DELAY_MS * Math.pow(2, attempt - 1));
    }
  }
}

export async function uploadFileToBlob(sasUrl, file, onProgress) {
  const CHUNK_SIZE = 4 * 1024 * 1024; // 4MB chunks
  const MAX_SINGLE_UPLOAD = 100 * 1024 * 1024; // 100MB threshold for chunked upload

  // For small files, use simple PUT
  if (file.size <= MAX_SINGLE_UPLOAD) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', sasUrl, true);
      xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) {
          onProgress((e.loaded / e.total) * 100);
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new Error(`Upload failed (HTTP ${xhr.status})`));
      };

      xhr.onerror = () => reject(new Error('Upload network error — please check your connection and try again'));
      xhr.send(file);
    });
  }

  // For large files, use chunked block upload
  const blockIds = [];
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
  let uploadedBytes = 0;

  // Upload each chunk as a block (with per-block retry)
  for (let i = 0; i < totalChunks; i++) {
    const start = i * CHUNK_SIZE;
    const end = Math.min(start + CHUNK_SIZE, file.size);
    const chunk = file.slice(start, end);
    const blockId = btoa(String(i).padStart(6, '0'));
    blockIds.push(blockId);

    const blockUrl = `${sasUrl}&comp=block&blockid=${encodeURIComponent(blockId)}`;

    await putWithRetry(
      blockUrl,
      chunk,
      { 'Content-Type': 'application/octet-stream' },
      `Block ${i + 1}/${totalChunks}`
    );

    uploadedBytes += chunk.size;
    if (onProgress) onProgress((uploadedBytes / file.size) * 100);
  }

  // Commit the blocks (with retry)
  const blockListXml = `<?xml version="1.0" encoding="utf-8"?><BlockList>${blockIds.map(id => `<Latest>${id}</Latest>`).join('')}</BlockList>`;
  const commitUrl = `${sasUrl}&comp=blocklist`;

  await putWithRetry(
    commitUrl,
    blockListXml,
    { 'Content-Type': 'application/xml', 'x-ms-blob-content-type': file.type || 'application/octet-stream' },
    'Block list commit'
  );
}


// ============================================================
// SUBMISSIONS LISTING API (Admin)
// ============================================================

/**
 * List all submissions.
 * WIRE: GET /api/submissions → 
 *   1. List all "folders" (blob prefixes) in "founders-submissions" container
 *   2. For each, read submission.json
 *   3. Return array sorted by timestamp desc
 */
export async function listSubmissions() {
  const res = await fetch(`${API_BASE}/submissions`);
  if (!res.ok) throw new Error('Failed to load submissions');
  return await res.json();
}

/**
 * Get a single submission with file download URLs.
 * WIRE: GET /api/submissions/:id →
 *   1. Read submission.json from the submission folder
 *   2. Generate read-only SAS URLs for logo + video blobs
 *   3. Return full submission data with download URLs
 */
export async function getSubmission(submissionId) {
  const res = await fetch(`${API_BASE}/submissions/${submissionId}`);
  if (!res.ok) throw new Error('Failed to load submission');
  return await res.json();
}

/**
 * Export all submissions as CSV.
 * WIRE: GET /api/submissions/export →
 *   1. Read all submission.json files
 *   2. Flatten to CSV format
 *   3. Return as downloadable CSV
 */
export async function exportSubmissions() {
  const res = await fetch(`${API_BASE}/submissions/export`);
  if (!res.ok) throw new Error('Export failed');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `founders-award-submissions-${new Date().toISOString().slice(0,10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Delete a submission and all its files.
 * WIRE: DELETE /api/submissions/:id
 */
export async function deleteSubmission(submissionId) {
  const res = await fetch(`${API_BASE}/submissions/${submissionId}`, {
    method: 'DELETE'
  });
  if (!res.ok) throw new Error('Delete failed');
  return await res.json();
}

/**
 * Generate a public shareable link for a file.
 * Returns a clean URL that redirects to the blob.
 * WIRE: POST /api/submissions/:id?action=share
 */
export async function shareSubmission(submissionId, fileType) {
  const res = await fetch(`${API_BASE}/submissions/${submissionId}?action=share`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fileType }) // 'logo' or 'video'
  });
  if (!res.ok) throw new Error('Share link generation failed');
  const data = await res.json();
  // Convert relative URL to full URL
  if (data.shareUrl && data.shareUrl.startsWith('/')) {
    data.shareUrl = window.location.origin + data.shareUrl;
  }
  return data;
}


// ============================================================
// UTILITY FUNCTIONS
// ============================================================

export function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

export function formatTimestamp(isoString) {
  const d = new Date(isoString);
  return d.toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
    timeZone: 'America/New_York',
    timeZoneName: 'short'
  });
}

export function isDeadlinePassed(deadline) {
  return new Date() > new Date(deadline);
}

export function sanitizeOrgName(name) {
  return name.replace(/[^a-zA-Z0-9\s-]/g, '').trim().replace(/\s+/g, '-').toLowerCase();
}
